import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { usageAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { AlertCircle, Loader2, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface TenantUsageData {
  tenantId: string;
  tenantSubdomain: string;
  totalQueries: number;
  totalTokens: number;
  totalCost: number;
  successRate: number;
  lastQueryDate: string;
}

const TenantUsagePage: React.FC = () => {
  const { isConfigured } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(25);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const { data, isLoading, error } = useQuery(
    ['tenant-usage', page, startDate, endDate],
    () => usageAPI.getTenantGroupedUsage({
      page: page + 1,
      limit: rowsPerPage,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    }),
    {
      enabled: isConfigured,
      keepPreviousData: true,
    }
  );

  const handleViewTenantDetails = (tenantId: string) => {
    navigate(`/usage/tenant/${tenantId}`, { 
      state: { startDate, endDate } 
    });
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  if (!isConfigured) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Please complete the initial setup to view usage data.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Usage by Tenant</h1>
        <p className="text-muted-foreground">
          Monitor usage metrics and costs grouped by tenant
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Usage Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load usage data. Please try again.
              </AlertDescription>
            </Alert>
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead className="text-right">Total Queries</TableHead>
                      <TableHead className="text-right">Total Tokens</TableHead>
                      <TableHead className="text-right">Total Cost</TableHead>
                      <TableHead className="text-right">Success Rate</TableHead>
                      <TableHead>Last Query</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.data.map((tenant: TenantUsageData) => (
                      <TableRow key={tenant.tenantId}>
                        <TableCell className="font-medium">
                          {tenant.tenantSubdomain}
                        </TableCell>
                        <TableCell className="text-right">
                          {tenant.totalQueries.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {tenant.totalTokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          ${tenant.totalCost.toFixed(4)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge 
                            variant={tenant.successRate >= 90 ? 'default' : 
                                   tenant.successRate >= 70 ? 'secondary' : 'destructive'}
                          >
                            {tenant.successRate.toFixed(1)}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(tenant.lastQueryDate), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewTenantDetails(tenant.tenantId)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Page {page + 1} of {data.pages || 1}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChangePage(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChangePage(page + 1)}
                    disabled={!data || page >= data.pages - 1}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No usage data found for the selected period.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantUsagePage;